import DropdownMenu from 'common/components/drop-downs/drop-down-menu';
import DropdownMenuButton from 'common/components/drop-downs/dropdown-menu-button';
import { useDispatch } from 'react-redux';
import { WebhookStatus } from '../webhook-status';
import {
  useUpdateWebhookMutation,
  useValidateWebhookEndpointMutation
} from 'app/apis/commandApi';
import { onSuccess } from 'features/toast/toast.slice';
import { Webhook } from '../webhooks.models';
import {
  createUpdateCacheKey,
  createValidationEndpointCacheKey
} from '../webhook-cache-keys';
import { useAuth } from 'features/auth/hooks/useAuth';
import { onWebhookToBeDeletedChanged } from './webhooks-table.slice';
import DropdownMenuAnchor from 'common/components/drop-downs/dropdown-menu-anchor';
import { useState } from 'react';

interface WebhookTableActionsProps {
  webhook: Webhook;
}

export default function WebhooksTableActions({
  webhook
}: WebhookTableActionsProps) {
  const dispatch = useDispatch();
  const { isSuperUser } = useAuth();
  const isDisabled = webhook.isInternalWebhook && !isSuperUser;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [updateWebhookMutation] = useUpdateWebhookMutation({
    fixedCacheKey: createUpdateCacheKey(webhook.id)
  });

  const onDisableWebhook = () => {
    const disabledWebhook: Webhook = {
      ...webhook,
      status: WebhookStatus.DISABLED
    };

    void updateWebhookMutation(disabledWebhook).then(() =>
      dispatch(
        onSuccess({
          title: 'Webhook Disabled',
          description: `The Webhook '${webhook.name}' has been disabled successfully.`
        })
      )
    );
  };

  const [validateWebhookEndpointMutation] = useValidateWebhookEndpointMutation({
    fixedCacheKey: createValidationEndpointCacheKey(webhook.id)
  });

  const onEnableWebhook = () => {
    void validateWebhookEndpointMutation({
      siteId: webhook.siteId,
      url: webhook.webhookUrl
    })
      .unwrap()
      .then(() => {
        const updatedWebhook: Webhook = {
          ...webhook,
          status: WebhookStatus.VALID
        };

        void updateWebhookMutation(updatedWebhook)
          .unwrap()
          .then(() =>
            dispatch(
              onSuccess({
                title: 'Webhook Enabled',
                description: `The Webhook '${webhook.name}' has been enabled successfully.`
              })
            )
          );
      })
      .catch(() => {
        const updatedWebhook: Webhook = {
          ...webhook,
          status: WebhookStatus.INVALID
        };

        void updateWebhookMutation(updatedWebhook);
      });
  };

  return (
    <div className="text-sm w-full flex items-center justify-center relative">
      <DropdownMenu
        isDropdownOpen={isDropdownOpen}
        buttonClasses="rounded hover:bg-neutral-100 flex items-center justify-center"
        buttonIconClasses="ra-icons-generic-menu-1 text-primary-700 flex items-center justify-center text-lg"
        dropdownMenuClasses="z-40 -top-2 right-11 w-40"
        containerClasses="flex justify-center relative"
        isDisabled={isDisabled}
        title={
          isDisabled
            ? 'Only administrators can action this Webhook'
            : 'Click to see the available actions for this Webhook'
        }
        onDropdownClicked={() => setIsDropdownOpen(!isDropdownOpen)}
        onClickAway={() => setIsDropdownOpen(false)}
      >
        {webhook.status === WebhookStatus.DISABLED ? (
          <DropdownMenuButton
            identifier="enable-webhook"
            buttonClasses="flex items-center"
            iconClasses="ra-icons-generic-check mr-2 text-neutral-500 group-hover:text-success-400 transition-all flex items-center w-fit text-lg"
            onClick={onEnableWebhook}
          >
            <span>Enable</span>
          </DropdownMenuButton>
        ) : (
          <DropdownMenuButton
            identifier="disable-webhook"
            buttonClasses="flex items-center"
            iconClasses="ra-icons-generic-minus mr-2 text-neutral-500 border-2 rounded-full border-neutral-500 group-hover:text-error-300 group-hover:border-error-300 transition-all flex items-center rotate-45 w-fit"
            onClick={onDisableWebhook}
          >
            <span>Disable</span>
          </DropdownMenuButton>
        )}
        {isSuperUser ? (
          <DropdownMenuAnchor
            to={`/settings/webhooks/${webhook.id}/history`}
            identifier="view-history"
            anchorClasses="flex items-center"
            iconClasses="ra-icons-generic-calendar mr-2 text-neutral-500 group-hover:text-success-400 transition-all flex items-center w-fit text-lg"
          >
            <span>View History</span>
          </DropdownMenuAnchor>
        ) : (
          <> </>
        )}
        <DropdownMenuButton
          identifier="delete-webhook"
          buttonClasses="flex items-center"
          iconClasses="ra-icons-generic-bin mr-2 text-neutral-500 group-hover:text-error-300 transition-all flex items-center w-fit text-lg"
          onClick={() => dispatch(onWebhookToBeDeletedChanged(webhook))}
        >
          <span>Delete</span>
        </DropdownMenuButton>
      </DropdownMenu>
    </div>
  );
}
